import React, { useState, useEffect, useCallback } from "react";
import {
  Modal, Box, TextField, Button, CircularProgress, Typography,
  Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, IconButton, 
  Card, CardContent, Grid, Chip, Divider, styled, alpha, Fade, Slide
} from "@mui/material";
import {
  Delete, Edit, Add, PhotoCamera, Close, Category,
  Warning, CheckCircle
} from "@mui/icons-material";
import {
  collection, query, getDocs, doc, setDoc, where, 
  updateDoc, writeBatch, deleteDoc
} from "firebase/firestore";
import { db, storage } from "../firebase-config";
import {
  ref, uploadBytes, getDownloadURL, deleteObject
} from "firebase/storage";
import Collections from "../collections";
import appTheme from "../theme";

// **STYLED COMPONENTS WITH LUXURY THEME**
const StyledModal = styled(Modal)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const ModalContainer = styled(Box)({
  width: '95%',
  maxWidth: '600px',
  maxHeight: '90vh',
  backgroundColor: appTheme.colors.surface,
  borderRadius: appTheme.borderRadius.rounded,
  boxShadow: appTheme.shadows.dramatic,
  overflow: 'hidden',
  position: 'relative',
});

const ModalHeader = styled(Box)({
  background: appTheme.gradients.hero,
  color: appTheme.colors.champagne,
  padding: `${appTheme.spacing.lg} ${appTheme.spacing.xl}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const ModalContent = styled(Box)({
  padding: appTheme.spacing.xl,
  maxHeight: 'calc(90vh - 120px)',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: appTheme.colors.surfaceElevated,
    borderRadius: appTheme.borderRadius.soft,
  },
  '&::-webkit-scrollbar-thumb': {
    background: appTheme.colors.primary,
    borderRadius: appTheme.borderRadius.soft,
    '&:hover': {
      background: appTheme.colors.deepTeal,
    },
  },
});

const ActionButton = styled(Button)(({ variant, color }) => ({
  borderRadius: appTheme.borderRadius.medium,
  textTransform: 'none',
  fontWeight: appTheme.typography.fontWeights.semibold,
  padding: `${appTheme.spacing.sm} ${appTheme.spacing.lg}`,
  boxShadow: variant === 'contained' ? appTheme.shadows.soft : 'none',
  transition: appTheme.transitions.luxury,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: variant === 'contained' ? appTheme.shadows.medium : appTheme.shadows.soft,
  },
}));

const CategoryCard = styled(Card)({
  borderRadius: appTheme.borderRadius.medium,
  boxShadow: appTheme.shadows.whisper,
  border: `1px solid ${appTheme.colors.borderSubtle}`,
  marginBottom: appTheme.spacing.md,
  transition: appTheme.transitions.smooth,
  '&:hover': {
    boxShadow: appTheme.shadows.soft,
    transform: 'translateY(-1px)',
    borderColor: appTheme.colors.primary,
  },
});

const ImageUploadArea = styled(Box)({
  border: `2px dashed ${appTheme.colors.borderPrimary}`,
  borderRadius: appTheme.borderRadius.medium,
  padding: appTheme.spacing.xl,
  textAlign: 'center',
  backgroundColor: appTheme.colors.surfaceLuxury,
  cursor: 'pointer',
  transition: appTheme.transitions.smooth,
  '&:hover': {
    borderColor: appTheme.colors.primary,
    backgroundColor: alpha(appTheme.colors.primary, 0.05),
  },
  '&.has-image': {
    border: `2px solid ${appTheme.colors.success}`,
    backgroundColor: alpha(appTheme.colors.success, 0.05),
  },
});

const AddCategoryModal = ({ open, onClose }) => {
  // **STATE MANAGEMENT**
  const [formData, setFormData] = useState({
    category: '',
    image: null,
    imagePreview: null,
  });
  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // **DELETE CONFIRMATION STATE**
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    category: null,
    itemsInCategory: [],
    migrationCategory: '',
    loading: false,
  });

  // **SAFE HELPERS**
  const safeToFixed = (value, decimals = 2) => {
    if (value === undefined || value === null || isNaN(value)) return '0.00';
    return Number(value).toFixed(decimals);
  };

  // **FETCH CATEGORIES**
  const fetchCategories = useCallback(async () => {
    try {
      const categoryQuery = query(collection(db, Collections.INVENTORY_CATEGORY));
      const querySnapshot = await getDocs(categoryQuery);
      const categoriesList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCategories(categoriesList);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories');
    }
  }, []);

  // **FETCH ITEMS IN CATEGORY**
  const fetchItemsInCategory = async (categoryId) => {
    try {
      const itemsQuery = query(
        collection(db, Collections.INVENTORY_ITEMS),
        where("categoryId", "==", categoryId)
      );
      const querySnapshot = await getDocs(itemsQuery);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (err) {
      console.error('Error fetching items in category:', err);
      return [];
    }
  };

  useEffect(() => {
    if (open) {
      fetchCategories();
    }
  }, [open, fetchCategories]);

  // **FORM HANDLERS**
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate image
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('Image size must be less than 5MB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          image: file,
          imagePreview: e.target.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setFormData({
      category: '',
      image: null,
      imagePreview: null,
    });
    setEditingCategory(null);
    setError('');
    setSuccess('');
  };

  // **SUBMIT HANDLER**
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category.trim()) {
      setError('Please enter a category name');
      return;
    }

    if (!editingCategory && !formData.image) {
      setError('Please select an image for the category');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (editingCategory) {
        // **UPDATE EXISTING CATEGORY**
        const categoryDoc = doc(db, Collections.INVENTORY_CATEGORY, editingCategory.id);
        const updateData = {
          category: formData.category.trim(),
          updatedAt: new Date().toISOString(),
        };

        if (formData.image) {
          // Delete old image
          if (editingCategory.image) {
            const oldImageRef = ref(storage, `categories/${editingCategory.id}.jpg`);
            await deleteObject(oldImageRef).catch(() => {});
          }

          // Upload new image
          const newImageRef = ref(storage, `categories/${editingCategory.id}.jpg`);
          await uploadBytes(newImageRef, formData.image);
          updateData.image = await getDownloadURL(newImageRef);
        }

        await setDoc(categoryDoc, updateData, { merge: true });
        setSuccess('Category updated successfully!');
      } else {
        // **CREATE NEW CATEGORY**
        const newDocRef = doc(collection(db, Collections.INVENTORY_CATEGORY));
        const imageRef = ref(storage, `categories/${newDocRef.id}.jpg`);
        
        await uploadBytes(imageRef, formData.image);
        const downloadURL = await getDownloadURL(imageRef);

        const categoryData = {
          id: newDocRef.id,
          category: formData.category.trim(),
          image: downloadURL,
          createdAt: new Date().toISOString(),
          updatedAt: null,
        };

        await setDoc(newDocRef, categoryData);
        setSuccess('Category added successfully!');
      }

      resetForm();
      fetchCategories();
      
      // Auto-close success message
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving category:', err);
      setError('Failed to save category. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // **EDIT HANDLER**
  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      category: category.category,
      image: null,
      imagePreview: category.image,
    });
  };

  // **DELETE HANDLERS**
  const handleDeleteClick = async (category) => {
    const items = await fetchItemsInCategory(category.id);
    setDeleteDialog({
      open: true,
      category,
      itemsInCategory: items,
      migrationCategory: '',
      loading: false,
    });
  };

  const handleDeleteConfirm = async () => {
    const { category, itemsInCategory, migrationCategory } = deleteDialog;
    
    if (itemsInCategory.length > 0 && !migrationCategory) {
      setError('Please select a category to migrate items to');
      return;
    }

    setDeleteDialog(prev => ({ ...prev, loading: true }));

    try {
      // Migrate items if needed
      if (itemsInCategory.length > 0 && migrationCategory) {
        const batch = writeBatch(db);
        itemsInCategory.forEach(item => {
          const itemRef = doc(db, Collections.INVENTORY_ITEMS, item.id);
          batch.update(itemRef, { categoryId: migrationCategory });
        });
        await batch.commit();
      }

      // Delete category
      await deleteDoc(doc(db, Collections.INVENTORY_CATEGORY, category.id));

      // Delete image from storage
      if (category.image) {
        const imageRef = ref(storage, `categories/${category.id}.jpg`);
        await deleteObject(imageRef).catch(() => {});
      }

      setDeleteDialog({ open: false, category: null, itemsInCategory: [], migrationCategory: '', loading: false });
      setSuccess('Category deleted successfully!');
      fetchCategories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting category:', err);
      setError('Failed to delete category. Please try again.');
      setDeleteDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // **CLOSE HANDLER**
  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <>
      <StyledModal open={open} onClose={handleClose}>
        <Fade in={open}>
          <ModalContainer>
            {/* **HEADER** */}
            <ModalHeader>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Category sx={{ fontSize: '1.8rem' }} />
                <Typography variant="h5" sx={{ fontWeight: appTheme.typography.fontWeights.bold }}>
                  {editingCategory ? 'Edit Category' : 'Category Management'}
                </Typography>
              </Box>
              <IconButton
                onClick={handleClose}
                sx={{
                  color: 'inherit',
                  backgroundColor: alpha('#FFFFFF', 0.1),
                  '&:hover': {
                    backgroundColor: alpha('#FFFFFF', 0.2),
                    transform: 'scale(1.1)',
                  },
                }}
              >
                <Close />
              </IconButton>
            </ModalHeader>

            <ModalContent>
              {/* **SUCCESS/ERROR MESSAGES** */}
              {success && (
                <Chip
                  icon={<CheckCircle />}
                  label={success}
                  sx={{
                    backgroundColor: alpha(appTheme.colors.success, 0.1),
                    color: appTheme.colors.success,
                    marginBottom: appTheme.spacing.lg,
                    fontWeight: 600,
                  }}
                />
              )}

              {error && (
                <Chip
                  icon={<Warning />}
                  label={error}
                  sx={{
                    backgroundColor: alpha(appTheme.colors.error, 0.1),
                    color: appTheme.colors.error,
                    marginBottom: appTheme.spacing.lg,
                    fontWeight: 600,
                  }}
                />
              )}

              {/* **FORM SECTION** */}
              <Card sx={{
                borderRadius: appTheme.borderRadius.medium,
                boxShadow: appTheme.shadows.soft,
                marginBottom: appTheme.spacing.xl,
              }}>
                <CardContent sx={{ padding: appTheme.spacing.lg }}>
                  <Typography variant="h6" sx={{
                    color: appTheme.colors.primary,
                    fontWeight: appTheme.typography.fontWeights.bold,
                    marginBottom: appTheme.spacing.lg,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    <Add /> {editingCategory ? 'Update Category' : 'Add New Category'}
                  </Typography>

                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Category Name"
                          value={formData.category}
                          onChange={(e) => handleInputChange('category', e.target.value)}
                          required
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: appTheme.borderRadius.medium,
                              '&:hover fieldset': {
                                borderColor: appTheme.colors.primary,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: appTheme.colors.primary,
                                borderWidth: '2px',
                              },
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{
                          fontWeight: appTheme.typography.fontWeights.semibold,
                          marginBottom: appTheme.spacing.md,
                          color: appTheme.colors.textPrimary,
                        }}>
                          Category Image
                        </Typography>
                        
                        <input
                          type="file"
                          id="image-upload"
                          accept="image/*"
                          onChange={handleImageChange}
                          style={{ display: 'none' }}
                        />
                        
                        <label htmlFor="image-upload">
                          <ImageUploadArea className={formData.imagePreview ? 'has-image' : ''}>
                            {formData.imagePreview ? (
                              <Box sx={{ position: 'relative' }}>
                                <img
                                  src={formData.imagePreview}
                                  alt="Category preview"
                                  style={{
                                    maxWidth: '100%',
                                    maxHeight: '200px',
                                    borderRadius: appTheme.borderRadius.medium,
                                    boxShadow: appTheme.shadows.soft,
                                  }}
                                />
                                <IconButton
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setFormData(prev => ({ ...prev, image: null, imagePreview: null }));
                                  }}
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    backgroundColor: alpha(appTheme.colors.error, 0.8),
                                    color: '#FFFFFF',
                                    '&:hover': {
                                      backgroundColor: appTheme.colors.error,
                                    },
                                  }}
                                  size="small"
                                >
                                  <Close fontSize="small" />
                                </IconButton>
                              </Box>
                            ) : (
                              <Box>
                                <PhotoCamera sx={{
                                  fontSize: '3rem',
                                  color: appTheme.colors.textSecondary,
                                  marginBottom: appTheme.spacing.sm,
                                }} />
                                <Typography variant="h6" sx={{
                                  color: appTheme.colors.textSecondary,
                                  fontWeight: appTheme.typography.fontWeights.medium,
                                }}>
                                  Click to upload image
                                </Typography>
                                <Typography variant="body2" sx={{
                                  color: appTheme.colors.textMuted,
                                  marginTop: appTheme.spacing.xs,
                                }}>
                                  Max size: 5MB • Formats: JPG, PNG, WEBP
                                </Typography>
                              </Box>
                            )}
                          </ImageUploadArea>
                        </label>
                      </Grid>
                    </Grid>

                    <Box sx={{
                      display: 'flex',
                      gap: 2,
                      justifyContent: 'flex-end',
                      marginTop: appTheme.spacing.xl,
                    }}>
                      {editingCategory && (
                        <ActionButton
                          variant="outlined"
                          onClick={resetForm}
                          sx={{
                            borderColor: appTheme.colors.textSecondary,
                            color: appTheme.colors.textSecondary,
                          }}
                        >
                          Cancel Edit
                        </ActionButton>
                      )}
                      
                      <ActionButton
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        sx={{
                          backgroundColor: editingCategory ? appTheme.colors.secondary : appTheme.colors.primary,
                          '&:hover': {
                            backgroundColor: editingCategory ? appTheme.colors.warmCopper : appTheme.colors.deepTeal,
                          },
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={20} sx={{ color: '#FFFFFF' }} />
                        ) : editingCategory ? (
                          'Update Category'
                        ) : (
                          'Add Category'
                        )}
                      </ActionButton>
                    </Box>
                  </form>
                </CardContent>
              </Card>

              {/* **EXISTING CATEGORIES** */}
              {categories.length > 0 && (
                <>
                  <Typography variant="h6" sx={{
                    color: appTheme.colors.primary,
                    fontWeight: appTheme.typography.fontWeights.bold,
                    marginBottom: appTheme.spacing.lg,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    📋 Existing Categories ({categories.length})
                  </Typography>

                  <Grid container spacing={2}>
                    {categories.map((category) => (
                      <Grid item xs={12} sm={6} key={category.id}>
                        <CategoryCard>
                          <CardContent sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            padding: `${appTheme.spacing.md} !important`,
                          }}>
                            {category.image && (
                              <img
                                src={category.image}
                                alt={category.category}
                                style={{
                                  width: 50,
                                  height: 50,
                                  objectFit: 'cover',
                                  borderRadius: appTheme.borderRadius.soft,
                                  boxShadow: appTheme.shadows.whisper,
                                }}
                              />
                            )}
                            
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" sx={{
                                fontWeight: appTheme.typography.fontWeights.semibold,
                                color: appTheme.colors.textPrimary,
                              }}>
                                {category.category}
                              </Typography>
                              <Typography variant="caption" sx={{
                                color: appTheme.colors.textMuted,
                              }}>
                                Created: {new Date(category.createdAt).toLocaleDateString('en-GB')}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                onClick={() => handleEdit(category)}
                                size="small"
                                sx={{
                                  backgroundColor: alpha(appTheme.colors.primary, 0.1),
                                  color: appTheme.colors.primary,
                                  '&:hover': {
                                    backgroundColor: alpha(appTheme.colors.primary, 0.2),
                                  },
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                              
                              <IconButton
                                onClick={() => handleDeleteClick(category)}
                                size="small"
                                sx={{
                                  backgroundColor: alpha(appTheme.colors.error, 0.1),
                                  color: appTheme.colors.error,
                                  '&:hover': {
                                    backgroundColor: alpha(appTheme.colors.error, 0.2),
                                  },
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </CardContent>
                        </CategoryCard>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
            </ModalContent>
          </ModalContainer>
        </Fade>
      </StyledModal>

      {/* **DELETE CONFIRMATION DIALOG** */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => !deleteDialog.loading && setDeleteDialog(prev => ({ ...prev, open: false }))}
        PaperProps={{
          sx: {
            borderRadius: appTheme.borderRadius.rounded,
            padding: appTheme.spacing.sm,
          }
        }}
      >
        <DialogTitle sx={{
          color: appTheme.colors.error,
          fontWeight: appTheme.typography.fontWeights.bold,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
          <Delete /> Confirm Delete
        </DialogTitle>
        
        <DialogContent>
          <DialogContentText sx={{ marginBottom: 2 }}>
            Are you sure you want to delete the category "{deleteDialog.category?.category}"?
          </DialogContentText>

          {deleteDialog.itemsInCategory.length > 0 && (
            <>
              <Typography variant="body1" sx={{ marginBottom: 2 }}>
                ⚠️ This category contains <strong>{deleteDialog.itemsInCategory.length} items</strong>.
                Please select a new category to migrate these items to:
              </Typography>

              <FormControl fullWidth>
                <InputLabel>Select Migration Category</InputLabel>
                <Select
                  value={deleteDialog.migrationCategory}
                  onChange={(e) => setDeleteDialog(prev => ({ ...prev, migrationCategory: e.target.value }))}
                  label="Select Migration Category"
                  sx={{
                    borderRadius: appTheme.borderRadius.medium,
                  }}
                >
                  {categories
                    .filter(cat => cat.id !== deleteDialog.category?.id)
                    .map(cat => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.category}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </>
          )}

          {error && (
            <Typography color="error" sx={{ marginTop: 2 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        
        <DialogActions sx={{ padding: appTheme.spacing.lg }}>
          <ActionButton
            onClick={() => setDeleteDialog(prev => ({ ...prev, open: false }))}
            disabled={deleteDialog.loading}
            sx={{
              color: appTheme.colors.textSecondary,
            }}
          >
            Cancel
          </ActionButton>
          
          <ActionButton
            onClick={handleDeleteConfirm}
            disabled={deleteDialog.loading || (deleteDialog.itemsInCategory.length > 0 && !deleteDialog.migrationCategory)}
            variant="contained"
            sx={{
              backgroundColor: appTheme.colors.error,
              '&:hover': {
                backgroundColor: appTheme.colors.warmCopper,
              },
            }}
          >
            {deleteDialog.loading ? (
              <CircularProgress size={20} sx={{ color: '#FFFFFF' }} />
            ) : (
              'Delete Category'
            )}
          </ActionButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddCategoryModal;
